import { parseISO, stepHours, todayInTimezone, type ISODate, type JobStep } from "@clarent/scheduling";
import { SHOP_TIMEZONE, todayISO } from "./today";

const short = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
const withYear = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

/** "Tue 27 Oct". Design system content rule: day, date, short month. */
export function formatDate(date: ISODate): string {
  return short.format(new Date(parseISO(date)));
}

/** As `formatDate`, adding the year only when it isn't this year. */
export function formatDateLong(date: ISODate): string {
  return date.slice(0, 4) === todayISO().slice(0, 4) ? formatDate(date) : withYear.format(new Date(parseISO(date)));
}

/** A timestamp's calendar date in the shop's timezone, formatted like `formatDateLong`. */
export function formatDay(at: Date): string {
  return formatDateLong(todayInTimezone(SHOP_TIMEZONE, at));
}

const clock = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: SHOP_TIMEZONE });

/** "Mon 19 Oct, 07:42" in the shop's timezone. */
export function formatDayTime(at: Date): string {
  return `${formatDay(at)}, ${clock.format(at)}`;
}

/** "Northgate CNC" → "NC", "Dave" → "D". */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join("");
}

/** "20 h" */
export function formatHours(hours: number): string {
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} h`;
}

/** "20 h · Painting 5d (+2 transit)" */
export function summariseSteps(steps: readonly JobStep[]): string {
  return steps
    .map((s) =>
      s.type === "machining"
        ? formatHours(stepHours(s))
        : `${s.name} ${s.leadDays}d${s.transitDaysOut + s.transitDaysBack > 0 ? ` (+${s.transitDaysOut + s.transitDaysBack} transit)` : ""}`,
    )
    .join(" · ");
}

/** ISO 8601 week number: weeks start Monday, week 1 holds the year's first Thursday. */
export function isoWeek(date: ISODate): number {
  const thursdayOf = (ms: number) => ms + (3 - ((new Date(ms).getUTCDay() + 6) % 7)) * 86_400_000;
  const thursday = thursdayOf(parseISO(date));
  const firstThursday = thursdayOf(Date.UTC(new Date(thursday).getUTCFullYear(), 0, 4));
  return 1 + Math.round((thursday - firstThursday) / (7 * 86_400_000));
}

/** Page header context: "Mon 19 Oct · Week 43". */
export function todayContext(today: ISODate): string {
  return `${formatDateLong(today)} · Week ${isoWeek(today)}`;
}

/** "W/C 26 Oct", the week commencing a Monday. */
export function weekCommencing(monday: ISODate): string {
  return `W/C ${formatDateLong(monday).replace(/^\w+ /, "")}`;
}

/**
 * Decision D44: a job's title is its part and quantity ("Bush ×200"), as the design shows.
 * Jobs without a part name fall back to the customer.
 */
export function jobTitle(job: { part?: string; quantity?: number; customerName: string }): string {
  if (!job.part) return job.customerName;
  return job.quantity ? `${job.part} ×${job.quantity}` : job.part;
}

export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}
